import { CommonModule } from '@angular/common';
import { Component, DOCUMENT, ElementRef, OnDestroy, Renderer2, effect, inject } from '@angular/core';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { getRouteProperty } from 'src/app/core/functions/route-signal.functions';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { LinkHandler } from 'src/app/shared/class/link-handler.class';

import {
  NAVIGATION_PATH_BY_CHOICE,
  SEARCH_QUERY_PARAM_KEYS,
  search_url_links
} from 'src/app/core/constants/dynamic-click-actions.constant';
import { ClickActionChoices } from 'src/app/home/models/home.model';

import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { HomePageRenderService } from 'src/app/home/services/home-page-render.service';

@Component({
  standalone: true,
  selector: 'webstore-dynamic-footer',
  templateUrl: './dynamic-footer.component.html',
  styleUrls: ['./dynamic-footer.component.scss'],
  host: { class: 'dynamic-theme-section' },
  imports: [CommonModule, RouterModule],
  providers: [OverlayService, BrowserApiService]
})
export class DynamicFooterComponent extends LinkHandler implements OnDestroy {
  public override renderer = inject(Renderer2);
  public override elementRef = inject(ElementRef);
  public override router = inject(Router);
  public override overlayService = inject(OverlayService);
  public override browserApiService = inject(BrowserApiService);

  private activatedRoute = inject(ActivatedRoute);
  private sanitizer = inject(DomSanitizer);
  private document = inject<Document>(DOCUMENT);
  private homePageRenderService = inject(HomePageRenderService);

  private destroyed$ = new ReplaySubject<boolean>(1);

  dynamicFooter: SafeHtml = '';

  // Field initializer - this is in injection context (like dynamic-header does)
  isVisible = getRouteProperty('footer', this.router, this.activatedRoute);

  // Old repository service signals removed - now using direct access from HomePageRenderService

  // Direct access to HomePageRenderService for processed data (no duplicate parsing)
  readonly isHomePageRendered = this.homePageRenderService.isRendered;
  readonly footerDataFromRenderer = this.homePageRenderService.footerData as any;

  constructor() {
    try {
      super();

      // Set up effects in constructor to ensure they're watching signals from the start
      try {
        // 1. STYLE EFFECTS FIRST - ensure styles are applied before data renders
        effect(() => {
          const isRendered = this.isHomePageRendered();

          if (isRendered && this.isBrowser()) {
          }
        });

        // 2. DATA EFFECTS SECOND - render data after styles are ready
        effect(() => {
          const footerValue = this.footerDataFromRenderer();

          if (footerValue) {
            // Apply data immediately after styles
            const processedHtml = this.convertDynamicLinksToHref(footerValue);
            this.dynamicFooter = this.sanitizer.bypassSecurityTrustHtml(processedHtml);
          }
        });

        // 3. VISIBILITY EFFECT - watch visibility changes and check for click actions
        effect(() => {
          if (this.isVisible()) {
            try {
              // âœ… Pass 'footer' as page name for UTM tracking (utm_source=footer)
              this.checkForClickActions('footer');
            } catch (error) {
              // Handle error silently
            }
          }
        });

      } catch (error) {
        // Handle effect creation error silently
      }
    } catch (error) {
      // Handle constructor error silently
    }
  }

  private handleClickAction(anchor: HTMLAnchorElement, clickActionChoice: string, actionValue: any): boolean {
    try {
      // Set default target if not specified
      const target = anchor!.getAttribute('target') || '_self';
      anchor!.setAttribute('target', target);

      const handlers: { [key: string]: () => string | undefined } = {
        [ClickActionChoices.modal]: () => undefined, // Skip modal actions
        [ClickActionChoices.search_keywords]: () => `/search/?q=${encodeURIComponent(actionValue.id)}`,
        [ClickActionChoices.product_flag]: () => `/products/?flag=${encodeURIComponent(actionValue.id)}`,
        [ClickActionChoices.link_brand]: () => {
          const brandPath = actionValue.url_path || actionValue.id;
          return brandPath ? `/brands/${encodeURIComponent(brandPath)}` : undefined;
        },
        [ClickActionChoices.link_collection]: () => {
          const collectionId = actionValue.id;
          return collectionId ? `/search?collection_ids=${encodeURIComponent(collectionId)}` : undefined;
        },
        [ClickActionChoices.link_url]: () => {
          let finalUrl = actionValue.id || '';
          // Trim any whitespace
          finalUrl = finalUrl.trim();
          // Check if it's already a valid URL
          if (finalUrl.match(/^https?:\/\//i)) {
            return finalUrl;
          }
          // If it starts with '/', it's an internal path
          if (finalUrl.startsWith('/')) {
            return finalUrl;
          }
          // Otherwise, treat as relative path
          return `/${finalUrl}`;
        },
        [ClickActionChoices.link_category]: () => {
          const categoryId = actionValue.id;
          return categoryId ? `/categories/${encodeURIComponent(categoryId)}` : undefined;
        },
        [ClickActionChoices.link_product]: () => {
          const productId = actionValue.id;
          return productId ? `/products/${encodeURIComponent(productId)}` : undefined;
        }
      };

      // Handle search URLs
      if (search_url_links.includes(clickActionChoice as ClickActionChoices)) {
        const basePath = NAVIGATION_PATH_BY_CHOICE[clickActionChoice as ClickActionChoices];
        const queryParam = SEARCH_QUERY_PARAM_KEYS[clickActionChoice as ClickActionChoices];
        return this.setAnchorHref(anchor!, `/${basePath}/?${queryParam}=${encodeURIComponent(actionValue.id)}`);
      }

      // Handle predefined actions
      const handler = handlers[clickActionChoice];
      if (handler) {
        const href = handler();
        return href ? this.setAnchorHref(anchor!, href) : false;
      }

      return false;
    } catch (error) {
      // Handle error silently
      return false;
    }
  }

  private setAnchorHref(anchor: HTMLAnchorElement, href: string): boolean {
    try {
      anchor.href = href;
      anchor.removeAttribute('data-click_action_choice');
      anchor.removeAttribute('data-click_action_value');
      return true;
    } catch (error) {
      // Handle error silently
      return false;
    }
  }

  private convertDynamicLinksToHref(html: string): string {
    try {
      if (!html) return '';

      const tempDiv = this.document.createElement('div');
      tempDiv.innerHTML = html;

      // Find all anchor tags
      const anchors = tempDiv.getElementsByTagName('a');

      for (let i = 0; i < anchors.length; i++) {
        const anchor = anchors[i];
        if (!anchor) continue;

        const clickActionChoice = anchor.getAttribute('data-click_action_choice');
        const clickActionValue = anchor.getAttribute('data-click_action_value');

        if (clickActionChoice && clickActionValue) {
          try {
            const actionValue = JSON.parse(clickActionValue);
            this.handleClickAction(anchor, clickActionChoice, actionValue);
          } catch (error) {
            // Failed to parse click action value
          }
        }
      }

      return tempDiv.innerHTML;
    } catch (error) {
      // Handle error silently
      return html; // Return original HTML if processing fails
    }
  }



  // Old duplicate parsing method removed - data now comes directly from HomePageRenderService

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  ngOnDestroy(): void {
    try {
      this.destroyed$.next(true);
      this.destroyed$.complete();
    } catch (error) {
      // Handle error silently
    }
  }
}


